const API_URL = '/api.php';
const ADMIN_CRED_KEY = 'lucky_draw_admin_credentials';
const STORAGE_KEY_SETTINGS = 'gamifycenter_settings';
const STORAGE_KEY_ENTRIES = 'gamifycenter_entries';
const STORAGE_KEY_WINNERS = 'gamifycenter_winners';

const hash = typeof window !== 'undefined' ? window.location.hash : '';
let initialGame = 'scratch';
if (hash.startsWith('#/spin')) initialGame = 'spin';
else if (hash.startsWith('#/peel')) initialGame = 'peel';
else if (hash.startsWith('#/slot')) initialGame = 'slot';
else if (hash.startsWith('#/flip')) initialGame = 'flip';

const defaultSettings = {
  campaignName: 'Mega Festive Bonanza',
  campaignStatus: 'active',
  prizes: ["₹50 Discount", "Free Premium Gift", "Better Luck Next Time", "Free Product Sample", "10% Cashback"],
  activeGame: initialGame,
  drawTime: null,
  resultsPublished: false,
  brandLogo: '',
  bgImage: 'https://images.unsplash.com/photo-1518605368461-1e1e38ce8ba9?auto=format&fit=crop&w=2000&q=80',
  displaySubtitle: 'Scan the QR code with your smartphone to participate in the current campaign and win exciting rewards!',
  successMessage: "You're officially in the Mega Draw.",
  requireSerialNumber: false,
  validSerialNumbers: [],
  usedSerialNumbers: [],
  // Enhanced Draw & Mode Settings
  soundEnabled: true,
  soundVolume: 0.7,
  musicEnabled: true,
  musicTheme: 'arcade_casino', // 'arcade_casino' | 'carnival_festive' | 'electronic_rush' | 'ambient_chill' | 'custom' | 'none'
  musicCustomUrl: '',
  musicVolume: 0.35,
  modeBehavior: 'fixed', // 'fixed' | 'random'
  megaDrawPrizes: ["1st Prize - Mega Bumper", "2nd Prize - Premium Gift", "3rd Prize - Exclusive Hamper", "Consolation Prize"],
  megaDrawFilter: 'all', // 'all' | 'unique' | 'scratch' | 'spin' | 'peel' | 'slot' | 'flip'
  homeDisplayMode: 'qr_participate', // 'qr_participate' | 'live_pot_draw' | 'both'
  livePotDrawBroadcast: null
};

// Safe localStorage loader
const loadLocal = (key, fallback) => {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (e) {
    return fallback;
  }
};

const saveLocal = (key, value) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {}
};

let cachedSettings = { ...defaultSettings, ...loadLocal(STORAGE_KEY_SETTINGS, {}) };
let cachedEntries = loadLocal(STORAGE_KEY_ENTRIES, []);
let cachedWinners = loadLocal(STORAGE_KEY_WINNERS, []);

let isFetching = false;

export const syncDB = async (force = false) => {
  if (isFetching && !force) return null;
  isFetching = true;
  try {
    const res = await fetch(`${API_URL}?action=get_all&t=${Date.now()}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    
    let updated = false;
    if (data.settings && JSON.stringify(cachedSettings) !== JSON.stringify(data.settings)) {
      cachedSettings = { ...defaultSettings, ...data.settings };
      saveLocal(STORAGE_KEY_SETTINGS, cachedSettings);
      updated = true;
    }
    if (data.entries && JSON.stringify(cachedEntries) !== JSON.stringify(data.entries)) {
      cachedEntries = data.entries;
      saveLocal(STORAGE_KEY_ENTRIES, cachedEntries);
      updated = true;
    }
    const serverWinners = data.winners || data.officialWinners;
    if (serverWinners && JSON.stringify(cachedWinners) !== JSON.stringify(serverWinners)) {
      cachedWinners = serverWinners;
      saveLocal(STORAGE_KEY_WINNERS, cachedWinners);
      updated = true;
    }
    
    if (updated || force) {
      window.dispatchEvent(new Event('settingsUpdated'));
    }
    return {
      settings: cachedSettings,
      entries: cachedEntries,
      officialWinners: getOfficialWinners()
    };
  } catch (e) {
    // API not reachable (dev server or static host) - localStorage continues to serve as reliable primary storage
    return {
      settings: cachedSettings,
      entries: cachedEntries,
      officialWinners: getOfficialWinners()
    };
  } finally {
    isFetching = false;
  }
};

if (typeof window !== 'undefined') {
  syncDB();
  setInterval(syncDB, 3000);

  // Cross-tab real-time synchronization
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY_SETTINGS) {
      const fresh = loadLocal(STORAGE_KEY_SETTINGS, null);
      if (fresh) {
        cachedSettings = { ...defaultSettings, ...fresh };
        window.dispatchEvent(new Event('settingsUpdated'));
      }
    } else if (e.key === STORAGE_KEY_ENTRIES) {
      cachedEntries = loadLocal(STORAGE_KEY_ENTRIES, []);
      window.dispatchEvent(new Event('settingsUpdated'));
    } else if (e.key === STORAGE_KEY_WINNERS) {
      cachedWinners = loadLocal(STORAGE_KEY_WINNERS, []);
      window.dispatchEvent(new Event('settingsUpdated'));
    }
  });
}

export const getSettings = () => {
  const fresh = loadLocal(STORAGE_KEY_SETTINGS, null);
  if (fresh) {
    cachedSettings = { ...defaultSettings, ...fresh };
  }
  return cachedSettings;
};

export const saveSettings = (settings) => {
  cachedSettings = { ...cachedSettings, ...settings };
  saveLocal(STORAGE_KEY_SETTINGS, cachedSettings);
  window.dispatchEvent(new Event('settingsUpdated'));
  fetch(`${API_URL}?action=save_settings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(cachedSettings)
  }).catch(() => {});
};

export const saveUserEntry = (entryData) => {
  const newEntry = { ...entryData, id: Date.now().toString(), createdAt: new Date().toISOString() };
  cachedEntries = [newEntry, ...cachedEntries];
  saveLocal(STORAGE_KEY_ENTRIES, cachedEntries);
  window.dispatchEvent(new Event('settingsUpdated'));
  fetch(`${API_URL}?action=add_entry`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(newEntry)
  }).catch(() => {});
  return newEntry;
};

export const getAllEntries = () => cachedEntries;

export const clearEntries = () => {
  cachedEntries = [];
  saveLocal(STORAGE_KEY_ENTRIES, cachedEntries);
  window.dispatchEvent(new Event('settingsUpdated'));
  fetch(`${API_URL}?action=clear_entries`, { method: 'POST' }).catch(() => {});
};

export const getOfficialWinners = () => {
  const oneWeekAgo = new Date().getTime() - (7 * 24 * 60 * 60 * 1000);
  return cachedWinners.filter(w => new Date(w.announcedAt).getTime() > oneWeekAgo);
};

export const saveOfficialWinners = (newWinners) => {
  const toAdd = newWinners.map(w => ({
    ...w,
    announcedId: Date.now().toString() + Math.random().toString(36).substring(2, 9),
    announcedAt: new Date().toISOString()
  }));
  cachedWinners = [...toAdd, ...cachedWinners];
  saveLocal(STORAGE_KEY_WINNERS, cachedWinners);
  window.dispatchEvent(new Event('settingsUpdated'));
  fetch(`${API_URL}?action=save_winners`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(cachedWinners)
  }).catch(() => {});
};

export const updateOfficialWinner = (announcedId, updates) => {
  cachedWinners = cachedWinners.map(w => w.announcedId === announcedId ? { ...w, ...updates } : w);
  saveLocal(STORAGE_KEY_WINNERS, cachedWinners);
  window.dispatchEvent(new Event('settingsUpdated'));
  fetch(`${API_URL}?action=save_winners`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(cachedWinners)
  }).catch(() => {});
};

export const deleteOfficialWinner = (announcedId) => {
  cachedWinners = cachedWinners.filter(w => w.announcedId !== announcedId);
  saveLocal(STORAGE_KEY_WINNERS, cachedWinners);
  window.dispatchEvent(new Event('settingsUpdated'));
  fetch(`${API_URL}?action=save_winners`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(cachedWinners)
  }).catch(() => {});
};

export const clearOfficialWinners = () => {
  cachedWinners = [];
  saveLocal(STORAGE_KEY_WINNERS, cachedWinners);
  window.dispatchEvent(new Event('settingsUpdated'));
  fetch(`${API_URL}?action=save_winners`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(cachedWinners)
  }).catch(() => {});
};

// --- Fraud Detection Helpers ---
export const validateSerialNumber = (serial) => {
  if (!cachedSettings.requireSerialNumber) return true;
  if (!serial) return false;
  const clean = String(serial).trim();
  const valid = cachedSettings.validSerialNumbers || [];
  const used = cachedSettings.usedSerialNumbers || [];
  const matchedValid = valid.find(v => String(v).trim().toLowerCase() === clean.toLowerCase());
  const isUsed = used.some(u => String(u).trim().toLowerCase() === clean.toLowerCase());
  return Boolean(matchedValid) && !isUsed;
};

export const markSerialNumberUsed = (serial) => {
  if (!cachedSettings.requireSerialNumber || !serial) return;
  const clean = String(serial).trim();
  if (!cachedSettings.usedSerialNumbers) cachedSettings.usedSerialNumbers = [];
  const isAlreadyUsed = cachedSettings.usedSerialNumbers.some(u => String(u).trim().toLowerCase() === clean.toLowerCase());
  if (!isAlreadyUsed) {
    const valid = cachedSettings.validSerialNumbers || [];
    const matched = valid.find(v => String(v).trim().toLowerCase() === clean.toLowerCase()) || clean;
    cachedSettings.usedSerialNumbers.push(matched);
    saveSettings(cachedSettings);
  }
};

// --- Admin Credentials Management ---
export const getAdminCredentials = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(ADMIN_CRED_KEY));
    if (parsed && parsed.username && parsed.password) {
      return parsed;
    }
  } catch (error) {
    console.error("Failed to parse admin credentials:", error);
  }
  return { username: 'Gamifycenter', password: 'Apnaevent.1@2026' };
};

export const saveAdminCredentials = ({ username, password }) => {
  localStorage.setItem(ADMIN_CRED_KEY, JSON.stringify({ username, password }));
};

// --- Staff Users & Station Allocation Storage ---
const STORAGE_KEY_STAFF = 'gamifycenter_staff_users';

export const DEFAULT_STAFF_MEMBERS = [
  {
    id: 'staff_1',
    name: 'Rajesh Sharma',
    role: 'Station Game Master',
    stationId: 'spin',
    stationName: 'Station 2 - Mega Spin Wheel',
    assignedEvent: 'Annual Carnival & Sports Gala 2026',
    pinCode: '1101',
    phone: '+91 98765 43210',
    status: 'on_duty',
    boothLocation: 'Main Arena Booth #2',
    notes: 'Handles high-volume visitor wheel spins & token validation.'
  },
  {
    id: 'staff_2',
    name: 'Priya Patel',
    role: 'Station Game Master',
    stationId: 'slot',
    stationName: 'Station 3 - Vegas Slot Machine',
    assignedEvent: 'Annual Carnival & Sports Gala 2026',
    pinCode: '2202',
    phone: '+91 98111 22334',
    status: 'on_duty',
    boothLocation: 'Main Arena Booth #3',
    notes: 'Manages dynamic token entry and reel spins.'
  },
  {
    id: 'staff_3',
    name: 'Amit Verma',
    role: 'Token Verifier & Gatekeeper',
    stationId: 'scratch',
    stationName: 'Station 1 - Lucky Scratch Cards',
    assignedEvent: 'Annual Carnival & Sports Gala 2026',
    pinCode: '3303',
    phone: '+91 98222 33445',
    status: 'on_duty',
    boothLocation: 'East Pavilion Booth #1',
    notes: 'Scans participant entry QR and issues token scratchers.'
  },
  {
    id: 'staff_4',
    name: 'Sunita Joseph',
    role: 'Station Game Master',
    stationId: 'peel',
    stationName: 'Station 4 - Peel & Reveal Cards',
    assignedEvent: 'Inter-School Sports Day 2026',
    pinCode: '4404',
    phone: '+91 98333 44556',
    status: 'on_duty',
    boothLocation: 'North Hall Kiosk',
    notes: 'Coordinates peel card reveals for visitors & students.'
  },
  {
    id: 'staff_5',
    name: 'Coach Vikram Singh',
    role: 'Chief Sports Referee',
    stationId: 'house_barometer',
    stationName: 'School Sports Barometer Arena',
    assignedEvent: 'Inter-House Athletic Championship',
    pinCode: '5505',
    phone: '+91 98444 55667',
    status: 'on_duty',
    boothLocation: 'Athletic Field Scoring Desk',
    notes: 'Logs track & field medals, awards points to house barometers.'
  }
];

export const getStaffUsers = () => loadLocal(STORAGE_KEY_STAFF, DEFAULT_STAFF_MEMBERS);

export const saveStaffUsers = (staffList) => {
  saveLocal(STORAGE_KEY_STAFF, staffList);
  window.dispatchEvent(new Event('staffUsersUpdated'));
};

export const addStaffUser = (staff) => {
  const current = getStaffUsers();
  const updated = [staff, ...current];
  saveStaffUsers(updated);
  return updated;
};

export const updateStaffUser = (id, updates) => {
  const current = getStaffUsers();
  const updated = current.map(s => s.id === id ? { ...s, ...updates } : s);
  saveStaffUsers(updated);
  return updated;
};

export const deleteStaffUser = (id) => {
  const current = getStaffUsers();
  const updated = current.filter(s => s.id !== id);
  saveStaffUsers(updated);
  return updated;
};